import {
  TOKEN_BUCKET_RATE,
  TOKEN_BUCKET_BURST,
  AUTH_HARDSTOP_K,
  CB_FAILURE_K,
  CB_COOLDOWN_MS,
  CONFIRMED_401_CODES,
} from './constants';

// ---------------------------------------------------------------------------
// TokenBucket — global rate killswitch
// ---------------------------------------------------------------------------
export class TokenBucket {
  private tokens: number;
  private lastRefill: number;

  constructor(
    private readonly rate: number = TOKEN_BUCKET_RATE,
    private readonly burst: number = TOKEN_BUCKET_BURST,
  ) {
    this.tokens = burst;
    this.lastRefill = Date.now();
  }

  private refill(now: number): void {
    const elapsed = (now - this.lastRefill) / 1000;
    this.tokens = Math.min(this.burst, this.tokens + elapsed * this.rate);
    this.lastRefill = now;
  }

  tryConsume(now: number = Date.now()): boolean {
    this.refill(now);
    if (this.tokens >= 1) {
      this.tokens -= 1;
      return true;
    }
    return false;
  }
}

// ---------------------------------------------------------------------------
// AuthGate — auth hardstop state machine
// ---------------------------------------------------------------------------
type AuthGateState = 'ok' | 'stopped';

export class AuthGate {
  private count = 0;
  state: AuthGateState = 'ok';

  on401or403(code?: string): void {
    if (this.state === 'stopped') return;
    if (code && (CONFIRMED_401_CODES as readonly string[]).includes(code)) {
      this.state = 'stopped';
      return;
    }
    this.count += 1;
    if (this.count >= AUTH_HARDSTOP_K) {
      this.state = 'stopped';
    }
  }

  // Direct stop — called on confirmed refresh failure
  markStopped(): void {
    this.state = 'stopped';
  }

  reset(): void {
    this.count = 0;
    this.state = 'ok';
  }
}

// ---------------------------------------------------------------------------
// CircuitBreakerRegistry — per-endpoint CB (5xx / timeout / network)
// ---------------------------------------------------------------------------
interface CBEntry {
  failures: number;
  openUntil: number;
}

export class CircuitBreakerRegistry {
  private registry = new Map<string, CBEntry>();

  private getOrCreate(key: string): CBEntry {
    if (!this.registry.has(key)) {
      this.registry.set(key, { failures: 0, openUntil: 0 });
    }
    return this.registry.get(key)!;
  }

  recordFailure(key: string, now: number = Date.now()): void {
    const entry = this.getOrCreate(key);
    entry.failures += 1;
    if (entry.failures >= CB_FAILURE_K) {
      entry.openUntil = now + CB_COOLDOWN_MS;
    }
  }

  recordSuccess(key: string): void {
    const entry = this.registry.get(key);
    if (!entry) return;
    entry.failures = 0;
    entry.openUntil = 0;
  }

  isOpen(key: string, now: number = Date.now()): boolean {
    const entry = this.registry.get(key);
    if (!entry) return false;
    return entry.openUntil > now;
  }
}

// ---------------------------------------------------------------------------
// Module-level singletons
// ---------------------------------------------------------------------------
export const tokenBucket = new TokenBucket();
export const authGate = new AuthGate();
export const cbRegistry = new CircuitBreakerRegistry();

// ---------------------------------------------------------------------------
// evaluateGuard — unified pre-request check
// ---------------------------------------------------------------------------
interface EvaluateGuardParams {
  endpointKey: string;
  now?: number;
}

type GuardBlockReason = 'auth_stopped' | 'circuit_breaker_open' | 'token_bucket_exhausted';

export interface EvaluateGuardResult {
  allowed: boolean;
  reason?: GuardBlockReason;
}

export function evaluateGuard({ endpointKey, now = Date.now() }: EvaluateGuardParams): EvaluateGuardResult {
  try {
    if (authGate.state === 'stopped') {
      return { allowed: false, reason: 'auth_stopped' };
    }
    if (cbRegistry.isOpen(endpointKey, now)) {
      return { allowed: false, reason: 'circuit_breaker_open' };
    }
    if (!tokenBucket.tryConsume(now)) {
      return { allowed: false, reason: 'token_bucket_exhausted' };
    }
    return { allowed: true };
  } catch {
    // fail-open: guard internal error must never crash the app
    return { allowed: true };
  }
}
